import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { Container } from "../components/ui-kit";

function fmt(d) {
  try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return d; }
}

export default function MediaDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/media/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center bg-sand"><Loader2 className="h-8 w-8 animate-spin text-emerald-800" /></div>;
  if (notFound || !item) return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-sand text-center">
      <p className="font-display text-2xl font-bold text-emerald-900">Article not found</p>
      <Link to="/media" className="rounded-full bg-emerald-900 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-800">Back to Media</Link>
    </div>
  );

  return (
    <div data-testid="media-detail-page">
      <section className="relative h-[46vh] min-h-[320px] overflow-hidden bg-[#022B25]">
        <img src={item.image} alt={item.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#022B25] via-[#022B25]/70 to-[#022B25]/30" />
        <Container className="relative flex h-full flex-col justify-end pb-10">
          <span className="inline-flex w-fit rounded-full bg-gold px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">{item.category}</span>
          <h1 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl">{item.title}</h1>
          <p className="mt-3 text-sm text-emerald-100/80">{item.author} · {fmt(item.date)} · {item.read_time}</p>
        </Container>
      </section>

      <section className="bg-sand">
        <Container className="py-12 lg:py-16">
          <Link to="/media" data-testid="media-back" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-gold"><ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Back to Media</Link>
          <article className="mx-auto mt-8 max-w-3xl">
            <p className="font-display text-xl italic leading-relaxed text-emerald-900">{item.excerpt}</p>
            <div className="mt-8 space-y-6">
              {(item.content || []).map((b, i) => {
                if (b.type === "h2") return <h2 key={i} className="font-display text-2xl font-bold text-emerald-950">{b.text}</h2>;
                if (b.type === "quote") return <blockquote key={i} className="border-s-4 border-gold bg-white/60 py-4 ps-6 font-display text-xl italic text-emerald-900">{b.text}</blockquote>;
                return <p key={i} className="text-[15px] leading-relaxed text-slate-700 sm:text-base">{b.text}</p>;
              })}
            </div>
            {item.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {item.tags.map((t) => <span key={t} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">#{t}</span>)}
              </div>
            )}
            <div className="mt-10 rounded-3xl border border-emerald-900/10 bg-white p-7 text-center hh-shadow">
              <p className="font-display text-xl font-bold text-emerald-950">Turn intention into impact.</p>
              <p className="mt-2 text-sm text-slate-600">Support a verified initiative today, every contribution is tracked and receipted.</p>
              <Link to="/programs" className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-gold-light">Support an Initiative</Link>
            </div>
          </article>
        </Container>
      </section>
    </div>
  );
}
